import axios, { AxiosError, type AxiosInstance } from 'axios';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/store/auth.store';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Не показывать тост с ошибкой — вызывающий код покажет её сам или она не важна. */
    silent?: boolean;
  }
}

const baseURL = import.meta.env.VITE_API_URL?.trim() || '';
const PREFIX = '/api/wholesaler';

// Страницы ловят ошибку и показывают e.message, а перехватчик ниже уже показал
// тот же текст — одинаковые тосты ошибок схлопываем по тексту.
const showError = toast.error;
toast.error = ((message, options) =>
  showError(message, { id: typeof message === 'string' ? `error:${message}` : undefined, ...options })) as typeof toast.error;

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 20_000,
});

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers['Authorization'] = `Bearer ${t}`;
  }
  return cfg;
});

// Единая очередь refresh: при пачке 401 рефрешим один раз, остальные ждут.
let refreshInflight: Promise<string | null> | null = null;
const pendingQueue: Array<(token: string | null) => void> = [];

async function refreshAccessTokenOnce(): Promise<string | null> {
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;
  try {
    const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
      `${baseURL}${PREFIX}/auth/refresh`,
      { refresh_token: refresh },
      { timeout: 10_000 },
    );
    useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<{ error?: string; code?: string }>) => {
    const status = err.response?.status;
    const original = err.config as (typeof err.config & { _retry?: boolean }) | undefined;
    const msg = err.response?.data?.error ?? err.message ?? 'Ошибка запроса';
    // Для страниц, которые показывают e.message: текст бэкенда вместо
    // «Request failed with status code 409» от axios.
    if (err.response?.data?.error) err.message = err.response.data.error;
    else if (!err.response && err.code !== 'ERR_CANCELED') err.message = 'Нет связи с сервером';

    if (status === 401 && original && !original._retry) {
      const url = original.url ?? '';
      if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register')) {
        useAuthStore.getState().logout();
        return Promise.reject(err);
      }

      original._retry = true;

      if (refreshInflight) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((token) => {
            if (!token) return reject(err);
            original.headers = original.headers ?? {};
            (original.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
            resolve(api(original));
          });
        });
      }

      refreshInflight = refreshAccessTokenOnce();
      const newToken = await refreshInflight;
      refreshInflight = null;

      const queue = pendingQueue.splice(0);
      for (const cb of queue) cb(newToken);

      if (!newToken) {
        useAuthStore.getState().logout();
        toast.error('Сессия истекла, войдите ещё раз');
        return Promise.reject(err);
      }
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
      return api(original);
    }

    if (status === 401) {
      useAuthStore.getState().logout();
      toast.error('Сессия истекла, войдите ещё раз');
    } else if (original?.silent) {
      // Вызывающий код сам решает, что показать.
    } else if (status && status >= 400 && status < 500) {
      toast.error(msg);
    } else if (status && status >= 500) {
      toast.error(`Ошибка сервера: ${msg}`);
    }
    return Promise.reject(err);
  },
);

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await api.get<T>(`${PREFIX}${url}`, { params });
  return data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${url}`, body);
  return data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.patch<T>(`${PREFIX}${url}`, body);
  return data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.put<T>(`${PREFIX}${url}`, body);
  return data;
}

export async function del<T>(url: string): Promise<T> {
  const { data } = await api.delete<T>(`${PREFIX}${url}`);
  return data;
}

/** Обновить access-токен (для переподключения сокета после отказа по истёкшему токену). */
export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshInflight) {
    refreshInflight = refreshAccessTokenOnce().finally(() => {
      refreshInflight = null;
    });
  }
  return refreshInflight;
}

// ─── Хелперы с опцией silent — общие с чатом и уведомлениями ──────────────

export async function apiGet<T>(path: string, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.get<T>(`${PREFIX}${path}`, { silent: opts?.silent });
  return data;
}

export async function apiPost<T>(path: string, body?: unknown, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${path}`, body, { silent: opts?.silent });
  return data;
}

export async function apiPatch<T>(path: string, body: unknown, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.patch<T>(`${PREFIX}${path}`, body, { silent: opts?.silent });
  return data;
}

export async function apiDelete<T>(path: string, opts?: { silent?: boolean; body?: unknown }): Promise<T> {
  const { data } = await api.delete<T>(`${PREFIX}${path}`, { silent: opts?.silent, data: opts?.body });
  return data;
}

/** Файл с полями формы; прогресс — доля отправленного (0–1). */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  opts?: { silent?: boolean; onProgress?: (ratio: number) => void },
): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${path}`, form, {
    silent: opts?.silent,
    // Голосовое или фото по мобильному интернету — дольше обычного запроса.
    timeout: 120_000,
    onUploadProgress: (e) => {
      if (e.total) opts?.onProgress?.(Math.min(1, e.loaded / e.total));
    },
  });
  return data;
}

/** Текст ошибки бэкенда для показа рядом с формой (вместо тоста). */
export function apiErrorMessage(err: unknown, fallback = 'Не удалось выполнить действие'): string {
  const e = err as AxiosError<{ error?: string }>;
  // 413 отдаёт nginx страницей, без нашего JSON.
  if (e?.response?.status === 413) return 'Файл слишком большой';
  return e?.response?.data?.error ?? (e?.message || fallback);
}
